/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import uuid from 'uuid';
import SearchUtils from '../../../utils/SearchUtils';
import SearchDropdownAppsItem from './SearchDropdownAppsItem';
import HorizontalRuler from '../../../components/HorizontalRuler';

type OwnProps = {
	searchHostContext: HostContext;
	onSubmit: (...args: Array<any>) => any;
	hostShowSearchDropdown: boolean;
	searchAppMetadataList: AppMetadata;
};

type SearchDropdownAppsProps = OwnProps & WrappedComponentProps & PropsFromRedux;
/*
 * This renders the Apps section in the Search Dropdown.
 */
class SearchDropdownApps extends Component<SearchDropdownAppsProps> {
	/*
	 * Returns the appropriate app title
	 * @return {string}
	 */
	getAppTitle(app) {
		let appTitle = `${app.name}`;
		const appType = SearchUtils.getAppTypeLocalizedKey(app.type);

		if (appType) {
			appTitle = `${app.name} (${this.props.intl.formatMessage({
				id: appType,
			})})`;
		}

		return appTitle;
	}

	getAutoCompleteItems(): Array<any> {
		const env = this.props.searchHostContext.environment;
		const limit = this.props.searchConfig?.ui?.autoComplete?.[env]?.appQuickLink?.limit ?? 3;

		const searchAutoCompleteIntentShortcuts: AppResultItem[] = this.props.searchAutoCompleteIntentShortcuts;
		let searchAutoCompleteQuickLinks: AppResultItem[] = this.props.searchAutoCompleteItems;

		// remove duplicate apps between intentShortcuts and quicklinks
		searchAutoCompleteQuickLinks = searchAutoCompleteQuickLinks
			.filter((quickLink: any) => {
				if (quickLink) {
					const sapCodes = quickLink.sapCode ? quickLink.sapCode.split(',') : [];
					const ffcIds = quickLink.ffcId ? quickLink.ffcId.split(',') : [];
					const match = searchAutoCompleteIntentShortcuts.find((intentShortcut: any) => {
						if (intentShortcut) {
							const sapCodeMatch = intentShortcut.sapCode && sapCodes.includes(intentShortcut.sapCode);
							const ffcIdMatch = intentShortcut.ffcId && ffcIds.includes(intentShortcut.ffcId);
							return sapCodeMatch || ffcIdMatch;
						}
						return false;
					});
					return !match;
				}
				return true;
			})
			.slice(0, limit);

		// Show the Search Dropdown with the list of search links

		searchAutoCompleteQuickLinks.map((item, index) => (item.score = index));

		const intentsAndQuickLinksCombinedList = [
			...searchAutoCompleteIntentShortcuts,
			...searchAutoCompleteQuickLinks,
		];
		return intentsAndQuickLinksCombinedList;
	}

	/**
	 * Returns the render for the Search Quick Links Items
	 * @returns <element>
	 */
	renderAutoCompleteApps() {
		const elements: any[] = [];

		const autoCompleteItems = this.getAutoCompleteItems();

		// sorting the combined list as per the score
		autoCompleteItems.length && autoCompleteItems.sort((a, b) => +a.score - +b.score);
		const autoCompleteElements = autoCompleteItems
			.map((item, index) => (
				<SearchDropdownAppsItem
					key={item.id || uuid.v4()}
					item={item}
					index={index}
					hostShowSearchDropdown={this.props.hostShowSearchDropdown}
					searchHostContext={this.props.searchHostContext}
				/>
			))
			.filter(Boolean);
		elements.push(...autoCompleteElements);

		// autoCompleteElements.length && elements.push(<HorizontalRuler />);

		return elements.length ? elements : null;
	}

	render() {
		const autoCompleteApps = this.renderAutoCompleteApps();
		return autoCompleteApps && autoCompleteApps.length > 0 ? (
			<div
				className='search-dropdown-apps-item-container'
				role='menu'
				aria-label={this.props.intl.formatMessage({ id: 'app.search.presearch.apps.group.title' })}
			>
				{
					<h5 className='search-dropdown-apps-group-title'>
						<FormattedMessage id={'app.search.presearch.apps.group.title'} />
					</h5>
				}
				{autoCompleteApps}
				{autoCompleteApps && autoCompleteApps.length > 0 ? <HorizontalRuler /> : null}
			</div>
		) : null;
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	searchAutoCompleteItems: search.searchAutoCompleteItems,
	searchAutoCompleteIntentShortcuts: search.searchAutoCompleteIntentShortcuts,
	searchAppMetadataList: search.searchAppMetadataList,
	searchConfig: search.searchConfig,
});

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropdownApps));
